import { useState } from 'react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { Button } from '@/components/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/shadcn/dialog'
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TikTokIcon,
  YoutubeIcon,
} from '@/components/ui/icons/icons-new'
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { TrendingUp, TrendingDown, Users, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'

type AccountGroup = RouterOutput['analytics']['accountOverview'][number]

const platformIcons: Record<string, typeof InstagramIcon> = {
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  tiktok: TikTokIcon,
  youtube: YoutubeIcon,
  facebook: FacebookIcon,
}

const platformLabels: Record<string, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  facebook: 'Facebook',
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '-'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function AccountCard({
  account,
  onRemove,
}: {
  account: AccountGroup
  onRemove: (platform: string, accountId: string) => void
}) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const PlatformIcon = platformIcons[account.platform]
  const label = platformLabels[account.platform] ?? account.platform

  const chronological = [...account.snapshots].reverse()
  const latest = account.snapshots[0]

  const followerData = chronological.map((s) => ({
    date: new Date(s.snapshotAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    followers: s.followers,
  }))

  const followerGrowth =
    chronological.length >= 2
      ? chronological[chronological.length - 1]!.followers - chronological[0]!.followers
      : null

  const isPositiveGrowth = followerGrowth != null && followerGrowth >= 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2.5">
          <div className="bg-card border-border flex h-8 w-8 items-center justify-center rounded-lg border">
            {PlatformIcon && <PlatformIcon width={16} height={16} color="var(--foreground)" />}
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-sm font-semibold">{label}</CardTitle>
            <p className="text-muted-foreground/60 truncate text-[11px]">{account.displayName}</p>
          </div>
          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground/40 hover:text-destructive -mr-1 shrink-0">
                <Trash2 size={14} />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove account</DialogTitle>
                <DialogDescription>
                  This will remove <strong>{account.displayName}</strong> and delete all its tracked
                  posts and analytics data. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    setConfirmOpen(false)
                    onRemove(account.platform, account.accountId)
                  }}>
                  Remove
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-0">
        {!latest ? (
          <p className="text-muted-foreground py-4 text-center text-sm">No data yet</p>
        ) : (
          <>
            <div className="mb-3 grid grid-cols-3 gap-3">
              <div>
                <p className="text-muted-foreground/70 text-[10px] uppercase tracking-wider">
                  Followers
                </p>
                <p className="text-foreground text-lg font-semibold tabular-nums">
                  {formatNumber(latest.followers)}
                </p>
                {followerGrowth != null && (
                  <div
                    className={`flex items-center gap-0.5 text-[11px] ${isPositiveGrowth ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {isPositiveGrowth ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {isPositiveGrowth ? '+' : ''}
                    {formatNumber(followerGrowth)}
                  </div>
                )}
              </div>
              <div>
                <p className="text-muted-foreground/70 text-[10px] uppercase tracking-wider">
                  Views
                </p>
                <p className="text-foreground text-lg font-semibold tabular-nums">
                  {formatNumber(latest.totalViews)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground/70 text-[10px] uppercase tracking-wider">
                  Posts
                </p>
                <p className="text-foreground text-lg font-semibold tabular-nums">
                  {formatNumber(account.postCount)}
                </p>
              </div>
            </div>

            {followerData.length > 1 && (
              <div className="border-border/60 -mx-2 border-t pt-3 md:-mx-4">
                <ResponsiveContainer width="100%" height={80}>
                  <AreaChart data={followerData}>
                    <defs>
                      <linearGradient
                        id={`grad-${account.platform}-${account.accountId}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1">
                        <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.12} />
                        <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" hide />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--popover)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        color: 'var(--popover-foreground)',
                        fontSize: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      }}
                      formatter={(value) => [formatNumber(value as number), 'Followers']}
                    />
                    <Area
                      type="monotone"
                      dataKey="followers"
                      stroke="var(--primary)"
                      strokeWidth={1.5}
                      fill={`url(#grad-${account.platform}-${account.accountId})`}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function AccountOverview({ platforms }: { platforms?: PublishablePlatformType[] }) {
  const utils = trpc.useUtils()
  const { data, isLoading, error } = trpc.analytics.accountOverview.useQuery({
    platforms: platforms?.length ? platforms : undefined,
    days: 30,
  })
  const removeAccount = trpc.analytics.removeAccount.useMutation({
    onSuccess: () => {
      utils.analytics.accountOverview.invalidate()
      utils.analytics.overview.invalidate()
      toast.success('Account removed')
    },
    onError: (err) => {
      toast.error('Failed to remove account', { description: err.message })
    },
  })

  const handleRemove = (plat: string, accountId: string) => {
    removeAccount.mutate({ platform: plat as PublishablePlatformType, accountId })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="border-primary/30 border-t-primary h-6 w-6 animate-spin rounded-full border-2" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <p className="text-foreground font-medium">Failed to load account data</p>
        <p className="text-muted-foreground text-sm">{error.message}</p>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
          <Users size={20} className="text-muted-foreground/40" />
        </div>
        <div>
          <p className="text-foreground font-medium">No account data yet</p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            Account metrics will appear after the analytics sync runs.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {data.map((account) => (
        <AccountCard
          key={`${account.platform}:${account.accountId}`}
          account={account}
          onRemove={handleRemove}
        />
      ))}
    </div>
  )
}
